// Utility functions for Firestore collection paths
// New structure: projects/{projectId} instead of users/{userId}/projects/{projectId}

/**
 * Get the path for a user document
 * @param {string} userId - The user ID
 * @returns {string} The Firestore path
 */
export const getUserPath = (userId) => `users/${userId}`;

/**
 * Get the path for user projects collection
 * @param {string} userId - The user ID
 * @returns {string} The Firestore path
 */
export const getUserProjectsPath = (userId) => `users/${userId}/userProjects`;

/**
 * Get the path for a specific user project document
 * @param {string} userId - The user ID
 * @param {string} projectId - The project ID
 * @returns {string} The Firestore path
 */
export const getUserProjectPath = (userId, projectId) => `users/${userId}/userProjects/${projectId}`;

/**
 * Get the path for a project document
 * @param {string} projectId - The project ID
 * @returns {string} The Firestore path
 */
export const getProjectPath = (projectId) => `projects/${projectId}`;

/**
 * Get the path for a project member document
 * @param {string} projectId - The project ID
 * @param {string} userId - The user ID
 * @returns {string} The Firestore path
 */
export const getProjectMemberPath = (projectId, userId) => `projects/${projectId}/members/${userId}`;

/**
 * Get the path for a project invited user document
 * @param {string} projectId - The project ID
 * @param {string} email - The email address
 * @returns {string} The Firestore path
 */

/**
 * Get the path for project audiences collection
 * @param {string} projectId - The project ID
 * @returns {string} The Firestore path
 */
export const getProjectAudiencesPath = (projectId) => `projects/${projectId}/audiences`;

/**
 * Get the path for a specific audience document
 * @param {string} projectId - The project ID
 * @param {string} audienceId - The audience ID
 * @returns {string} The Firestore path
 */

/**
 * Get the path for audience actors collection
 * @param {string} projectId - The project ID
 * @param {string} audienceId - The audience ID
 * @returns {string} The Firestore path
 */
export const getProjectAudienceActorsPath = (projectId, audienceId) => `projects/${projectId}/audiences/${audienceId}/actors`;

/**
 * Get the path for a specific actor document
 * @param {string} projectId - The project ID
 * @param {string} audienceId - The audience ID
 * @param {string} actorId - The actor ID
 * @returns {string} The Firestore path
 */
export const getProjectActorPath = (projectId, audienceId, actorId) => `projects/${projectId}/audiences/${audienceId}/actors/${actorId}`;

/**
 * Get the path for actor subcollections (fears, contexts, etc.)
 * @param {string} projectId - The project ID
 * @param {string} audienceId - The audience ID
 * @param {string} actorId - The actor ID
 * @param {string} subcollectionName - The subcollection name
 * @returns {string} The Firestore path
 */
export const getProjectActorSubcollectionPath = (projectId, audienceId, actorId, subcollectionName) => 
  `projects/${projectId}/audiences/${audienceId}/actors/${actorId}/${subcollectionName}`;

/**
 * Get the path for project tasks collection
 * @param {string} projectId - The project ID
 * @returns {string} The Firestore path
 */
export const getProjectTasksPath = (projectId) => `projects/${projectId}/tasks`;

/**
 * Get the path for project reviews collection
 * @param {string} projectId - The project ID
 * @returns {string} The Firestore path
 */
export const getProjectReviewsPath = (projectId) => `projects/${projectId}/reviews`;

/**
 * Get the path for project ratings collection
 * @param {string} projectId - The project ID
 * @returns {string} The Firestore path
 */
export const getProjectRatingsPath = (projectId) => `projects/${projectId}/ratings`;

/**
 * Get the path for project big numbers collection
 * @param {string} projectId - The project ID
 * @returns {string} The Firestore path
 */
export const getProjectBigNumbersPath = (projectId) => `projects/${projectId}/bignumbers`;

/**
 * Get the path for project study cases collection
 * @param {string} projectId - The project ID
 * @returns {string} The Firestore path
 */
export const getProjectStudyCasesPath = (projectId) => `projects/${projectId}/studycases`;

/**
 * Get the path for study case output subcollection
 * @param {string} projectId - The project ID
 * @param {string} studyCaseId - The study case ID
 * @returns {string} The Firestore path
 */
export const getProjectStudyCaseOutputPath = (projectId, studyCaseId) => `projects/${projectId}/studycases/${studyCaseId}/output`;

/**
 * Get the path for project social proofs collection
 * @param {string} projectId - The project ID
 * @returns {string} The Firestore path
 */
export const getProjectSocialProofsPath = (projectId) => `projects/${projectId}/socialproofs`;

/**
 * Get the path for a specific task document
 * @param {string} projectId - The project ID
 * @param {string} taskId - The task ID
 * @returns {string} The Firestore path
 */

/**
 * Get the path for project strategies collection
 * @param {string} projectId - The project ID
 * @returns {string} The Firestore path
 */
export const getProjectStrategiesPath = (projectId) => `projects/${projectId}/strategies`;

/**
 * Get the path for a specific strategy document
 * @param {string} projectId - The project ID
 * @param {string} strategyId - The strategy ID
 * @returns {string} The Firestore path
 */
export const getProjectStrategyPath = (projectId, strategyId) => `projects/${projectId}/strategies/${strategyId}`;

/**
 * Get the path for strategy blocks collection
 * @param {string} projectId - The project ID
 * @param {string} strategyId - The strategy ID
 * @returns {string} The Firestore path
 */
export const getProjectStrategyBlocksPath = (projectId, strategyId) => `projects/${projectId}/strategies/${strategyId}/blocks`;

/**
 * Get the path for a specific strategy block document
 * @param {string} projectId - The project ID
 * @param {string} strategyId - The strategy ID
 * @param {string} blockId - The block ID
 * @returns {string} The Firestore path
 */

/**
 * Get the path for block screens collection
 * @param {string} projectId - The project ID
 * @param {string} strategyId - The strategy ID
 * @param {string} blockId - The block ID
 * @returns {string} The Firestore path
 */
export const getProjectBlockScreensPath = (projectId, strategyId, blockId) => `projects/${projectId}/strategies/${strategyId}/blocks/${blockId}/screens`;

/**
 * Get the path for a specific screen document
 * @param {string} projectId - The project ID
 * @param {string} strategyId - The strategy ID
 * @param {string} blockId - The block ID
 * @param {string} screenId - The screen ID
 * @returns {string} The Firestore path
 */
